/**
 * City Content Section Component
 * Renders SEO-optimized content for city pages
 */

import React from 'react';
import { getCityData, formatCityName } from '../../config/cityData';

interface CityContentSectionProps {
  city: string;
  turfCount: number;
  areaName?: string | null;
  topTurfNames?: string[];
}

const CityContentSection: React.FC<CityContentSectionProps> = ({
  city,
  turfCount,
  areaName,
  topTurfNames = [],
}) => {
  const cityData = getCityData(city);
  const cityName = cityData?.name || formatCityName(city);
  const state = cityData?.state || 'India';
  const locationLabel = areaName ? `${areaName}, ${cityName}` : cityName;

  if (!cityData) {
    return null;
  }

  return (
    <section
      className="city-seo-content"
      style={{
        margin: '24px auto',
        maxWidth: '1120px',
        padding: '24px',
        borderRadius: '20px',
        background: 'linear-gradient(180deg, rgba(57,16,133,0.06) 0%, rgba(255,255,255,0.98) 100%)',
        border: '1px solid rgba(57,16,133,0.12)',
      }}
    >
      <h1
        style={{
          fontSize: '28px',
          fontWeight: 700,
          marginBottom: '12px',
          color: '#1a1a2e',
          lineHeight: '1.3',
        }}
      >
        Book Turf in {locationLabel}
      </h1>

      <p
        style={{
          fontSize: '16px',
          lineHeight: '1.7',
          marginBottom: '16px',
          color: '#333',
        }}
      >
        Explore <strong>{turfCount}+ verified turfs</strong> in {locationLabel}, {state} with
        photos, amenities, pricing and exact location details. Compare cricket and football
        venues, shortlist nearby options and book at venue price with no extra charges.
      </p>

      {topTurfNames.length > 0 && (
        <>
          <h2
            style={{
              fontSize: '20px',
              fontWeight: 600,
              margin: '20px 0 12px',
              color: '#1a1a2e',
            }}
          >
            Popular Turf Options in {locationLabel}
          </h2>
          <p
            style={{
              fontSize: '15px',
              lineHeight: '1.7',
              marginBottom: '14px',
              color: '#444',
            }}
          >
            Popular options people compare in {locationLabel} include{' '}
            <strong>{topTurfNames.join(', ')}</strong>.
          </p>
        </>
      )}

      <h2
        style={{
          fontSize: '20px',
          fontWeight: 600,
          margin: '20px 0 12px',
          color: '#1a1a2e',
        }}
      >
        Why players choose TurfBooking.in for {locationLabel}
      </h2>

      <ul
        style={{
          fontSize: '15px',
          lineHeight: '1.8',
          paddingLeft: '20px',
          marginBottom: '0',
          color: '#333',
        }}
      >
        <li>{turfCount}+ verified listings with photos, prices and amenities.</li>
        <li>Area-specific browsing to quickly find grounds near your location.</li>
        <li>Exact venue pricing with no hidden convenience charges.</li>
        <li>Useful details like address, dimensions, facilities and contact info before booking.</li>
      </ul>
    </section>
  );
};

export default CityContentSection;
